'use client';

import { PlayCircleIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';

interface CarbonCreditsVideoProps {
  currentLanguage: string;
}

const CarbonCreditsVideo = ({ currentLanguage }: CarbonCreditsVideoProps) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const content = {
    en: {
      title: 'How Carbon Credits Are Generated',
      subtitle: 'Understanding the Journey from Emission Reduction to Verified Credits',
      description:
        'Discover the complete lifecycle of carbon credit generation - from project implementation and emission reduction to third-party verification and market certification. Learn how enterprises and governments can leverage this mechanism for sustainable development.',
      videoUrl: 'https://www.youtube.com/embed/JXcp-E1bJzg',
      watchText: 'Watch Video',
      keyPoints: [
        'Project Development & Baseline Assessment',
        'Implementation & Emission Reduction',
        'Monitoring, Reporting & Verification (MRV)',
        'Third-Party Validation & Certification',
        'Credit Issuance & Market Trading',
      ],
    },
    hi: {
      title: 'कार्बन क्रेडिट कैसे उत्पन्न होते हैं',
      subtitle: 'उत्सर्जन कटौती से सत्यापित क्रेडिट तक की यात्रा को समझना',
      description:
        'कार्बन क्रेडिट उत्पादन के पूर्ण जीवनचक्र की खोज करें - परियोजना कार्यान्वयन और उत्सर्जन में कमी से लेकर तृतीय-पक्ष सत्यापन और बाजार प्रमाणन तक। जानें कि उद्यम और सरकारें सतत विकास के लिए इस तंत्र का लाभ कैसे उठा सकते हैं।',
      videoUrl: 'https://www.youtube.com/embed/JXcp-E1bJzg',
      watchText: 'वीडियो देखें',
      keyPoints: [
        'परियोजना विकास और आधार रेखा मूल्यांकन',
        'कार्यान्वयन और उत्सर्जन में कमी',
        'निगरानी, रिपोर्टिंग और सत्यापन (MRV)',
        'तृतीय-पक्ष मान्यता और प्रमाणन',
        'क्रेडिट जारी करना और बाजार व्यापार',
      ],
    },
    ar: {
      title: 'كيف يتم توليد أرصدة الكربون',
      subtitle: 'فهم الرحلة من خفض الانبعاثات إلى الاعتمادات المعتمدة',
      description:
        'اكتشف دورة الحياة الكاملة لتوليد أرصدة الكربون - من تنفيذ المشروع وخفض الانبعاثات إلى التحقق من طرف ثالث والشهادة السوقية. تعرف على كيفية استفادة المؤسسات والحكومات من هذه الآلية للتنمية المستدامة.',
      videoUrl: 'https://www.youtube.com/embed/JXcp-E1bJzg',
      watchText: 'مشاهدة الفيديو',
      keyPoints: [
        'تطوير المشروع وتقييم خط الأساس',
        'التنفيذ وخفض الانبعاثات',
        'المراقبة والإبلاغ والتحقق (MRV)',
        'التحقق والشهادة من طرف ثالث',
        'إصدار الائتمان والتداول في السوق',
      ],
    },
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {lang.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-2">
            {lang.subtitle}
          </p>
          <p className="text-lg text-gray-500 max-w-4xl mx-auto">
            {lang.description}
          </p>
        </div>

        {/* Video Container */}
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900 aspect-video">
            {!isPlaying ? (
              <div
                className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                onClick={() => setIsPlaying(true)}
              >
                {/* Thumbnail Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 to-blue-600/20" />
                
                {/* Play Button */}
                <div className="relative z-10 transform transition-transform group-hover:scale-110">
                  <PlayCircleIcon className="w-24 h-24 text-white opacity-90 group-hover:opacity-100" />
                </div>

                {/* Watch Text */}
                <div className="absolute bottom-8 left-0 right-0 text-center">
                  <span className="text-white text-xl font-semibold bg-black/50 px-6 py-3 rounded-full">
                    {lang.watchText}
                  </span>
                </div>
              </div>
            ) : (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`${lang.videoUrl}?autoplay=1`}
                title="Carbon Credits Generation Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </div>

        {/* Key Points */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lang.keyPoints.map((point, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-gray-700 font-medium leading-relaxed">
                    {point}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <a
            href="/get-started"
            className="inline-flex items-center px-8 py-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl"
          >
            Start Your Carbon Credit Journey
            <svg
              className="ml-2 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default CarbonCreditsVideo;